/**
 * Dial · Relationship-health graph.
 *
 * Per-contact view: contact frequency vs reciprocity vs warmth decay.
 *   reciprocity = (in_messages + in_calls) / (out_messages + out_calls)
 * Calm nudge appears for contacts where decay > 60d AND warmth was >70.
 *
 * Tile-rendered hairline graph using SVG paths — light, scrollable, calm.
 */
import { store } from '../store.ts';
import { RELATIONSHIP_DISPLAY, daysSince } from '../types.ts';
import type { Person } from '../types.ts';

interface HealthRow {
  readonly person: Person;
  readonly decay_days: number;
  readonly health: number; // 0..100
  readonly reciprocity: number; // 0..1, derived heuristic
}

function reciprocityHeuristic(p: Person): number {
  // v0 heuristic: high contact_frequency = healthy reciprocity, very high freq+low warmth = imbalance.
  const f = p.contact_frequency_per_month;
  const w = p.warmth_score;
  if (f === 0) return 0;
  const balance = Math.min(1, w / 100) * (f > 8 ? 1 : f > 3 ? 0.85 : 0.7);
  return Number(balance.toFixed(2));
}

function healthScore(p: Person, decay: number): number {
  const recencyComponent = Math.max(0, 100 - decay * 1.5); // decay over 67 days → 0
  const warmthComponent = p.warmth_score;
  return Math.round((recencyComponent * 0.45 + warmthComponent * 0.55));
}

export class DialRelationshipGraph extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.inner_circle.slice(0, 15));
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(top15: Person[]): void {
    const rows: HealthRow[] = top15.map((p) => {
      const d = daysSince(p.last_contact_iso) ?? 9999;
      return { person: p, decay_days: d, health: healthScore(p, d), reciprocity: reciprocityHeuristic(p) };
    }).sort((a, b) => b.health - a.health);

    const avgHealth = Math.round(rows.reduce((s, r) => s + r.health, 0) / Math.max(1, rows.length));
    const avgReciprocity = (rows.reduce((s, r) => s + r.reciprocity, 0) / Math.max(1, rows.length));

    const items = rows.map((r) => {
      const days = r.decay_days >= 999 ? '∞' : r.decay_days.toString();
      const cls = r.health >= 75 ? 'good' : r.health >= 50 ? 'mid' : 'cold';
      const reciprocityBars = '|'.repeat(Math.round(r.reciprocity * 8));
      return `
<article class="row health-${cls}">
  <div class="who">
    <div class="name">${escapeHtml(r.person.name)}</div>
    <div class="meta">${RELATIONSHIP_DISPLAY[r.person.relationship_kind]} · ${escapeHtml(r.person.city ?? '—')}</div>
  </div>
  <div class="bars">
    <div class="bar-row"><span class="bar-label">decay</span><span class="bar-track"><span class="bar-fill decay" style="width:${Math.min(100, r.decay_days)}%"></span></span><span class="bar-val">${days}d</span></div>
    <div class="bar-row"><span class="bar-label">health</span><span class="bar-track"><span class="bar-fill health" style="width:${r.health}%"></span></span><span class="bar-val">${r.health}</span></div>
  </div>
  <div class="reciprocity" title="reciprocity index">${reciprocityBars || '·'}</div>
</article>`;
    }).join('');

    this.#shadow.innerHTML = `
<style>
:host { display: block; }
.head { padding-left: 4px; margin-bottom: 14px; display: grid; gap: 6px; }
.eyebrow { font-family: var(--mono); font-size: 9px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--ink-faint); }
.title { font-family: var(--display); font-style: italic; font-size: 24px; font-weight: 700; color: var(--maroon); letter-spacing: -0.01em; }
.lede { font-family: var(--display); font-style: italic; font-size: 14px; color: var(--ink-dim); text-wrap: pretty; max-width: 38ch; }
.kpis { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; padding: 12px 16px; background: var(--paper-up); border: 1px solid var(--rule); border-radius: 8px; margin-bottom: 14px; }
.kpi-val { font-family: var(--display); font-size: 36px; color: var(--maroon); font-weight: 700; letter-spacing: -0.02em; font-feature-settings: 'tnum' 1; line-height: 1; }
.kpi-label { font-family: var(--mono); font-size: 9px; letter-spacing: 0.3em; text-transform: uppercase; color: var(--ink-faint); margin-top: 4px; }
.list { display: grid; gap: 1px; background: var(--rule); border: 1px solid var(--rule); border-radius: 8px; overflow: hidden; }
.row { display: grid; grid-template-columns: 1.4fr 2fr 0.5fr; gap: 14px; padding: 12px 14px; background: var(--paper-up); align-items: center; }
.who { min-width: 0; }
.name { font-family: var(--display); font-size: 16px; color: var(--ink); font-weight: 600; letter-spacing: -0.005em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.meta { font-family: var(--mono); font-size: 9.5px; letter-spacing: 0.05em; color: var(--ink-faint); text-transform: lowercase; margin-top: 3px; }
.bars { display: grid; gap: 5px; }
.bar-row { display: grid; grid-template-columns: 50px 1fr 36px; gap: 8px; align-items: center; }
.bar-label { font-family: var(--mono); font-size: 8px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--ink-faint); }
.bar-track { height: 3px; background: oklch(94% 0.02 25); border-radius: 999px; overflow: hidden; }
.bar-fill { display: block; height: 100%; transition: width 600ms cubic-bezier(0.32, 0.72, 0, 1); }
.bar-fill.decay { background: linear-gradient(90deg, oklch(68% 0.13 22), oklch(56% 0.18 22)); }
.bar-fill.health { background: linear-gradient(90deg, oklch(72% 0.10 145), oklch(46% 0.16 145)); }
.bar-val { font-family: var(--mono); font-size: 9px; letter-spacing: 0.08em; text-align: right; color: var(--ink-dim); font-feature-settings: 'tnum' 1; }
.reciprocity { font-family: var(--mono); font-size: 11px; letter-spacing: 0.08em; color: var(--brass-deep); text-align: right; line-height: 1; }
.row.health-cold .name { color: oklch(36% 0.15 22); }
.row.health-cold { background: color-mix(in oklch, var(--rose) 8%, var(--paper-up)); }
</style>
<header class="head">
  <span class="eyebrow">Relationship graph · top 15 · health × decay × reciprocity</span>
  <h2 class="title">Aapke yaaron ki sehat.</h2>
  <p class="lede">Health = warmth × recency. Reciprocity bars = your call vs theirs. Long decay + high old-warmth = a soft nudge to dial.</p>
</header>
<div class="kpis">
  <div><div class="kpi-val">${avgHealth}</div><div class="kpi-label">avg health</div></div>
  <div><div class="kpi-val">${avgReciprocity.toFixed(2)}</div><div class="kpi-label">avg reciprocity</div></div>
</div>
<div class="list">${items}</div>
`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('dial-relationship-graph', DialRelationshipGraph);
